/**
 * AdminTravelToolsDistance — admin panel for the Travel Tools "Distance & Routes" tab.
 *
 * Lets the admin manage city-pair transport routes (train · bus · car) without touching code.
 *
 * IMPORTANT — independent module:
 *   This tab reads/writes its OWN collections (`traveltools_distance_cities`,
 *   `traveltools_distance_routes`, `traveltools_distance_settings`). It is fully decoupled
 *   from the Darija Buddy "Transport" feature so one breaking does not take the other down.
 *   A one-shot `/admin/distance/migrate-from-buddy` endpoint can copy existing Buddy transport
 *   data into the Travel Tools collections if needed.
 *
 * Backend endpoints (all gated by verify_admin_token, sent as multipart/form-data):
 *   GET    /api/travel-tools/admin/distance/cities
 *   POST   /api/travel-tools/admin/distance/cities          (Form: name, name_ar, name_fr)
 *   DELETE /api/travel-tools/admin/distance/cities/{id}
 *   GET    /api/travel-tools/admin/distance/routes
 *   POST   /api/travel-tools/admin/distance/routes          (Form: from_city, to_city, transports, distance_km)
 *   PUT    /api/travel-tools/admin/distance/routes/{id}     (Form: same + is_active)
 *   DELETE /api/travel-tools/admin/distance/routes/{id}
 *   GET    /api/travel-tools/admin/distance/pricing-formula
 *   PUT    /api/travel-tools/admin/distance/pricing-formula (Form: title, formula_text, disclaimer, ...)
 *   POST   /api/travel-tools/admin/distance/migrate-from-buddy  (one-shot import from Buddy)
 *
 * Car / Taxi naming:
 *   The DB stores the legacy value `"type": "taxi"`. We display it as "🚗 Car / Taxi" in the
 *   dropdown and as "Car" in the per-route summary. No data migration — user confirmed.
 */
import React, { useEffect, useMemo, useState } from 'react';

// ─── Local primitives (duplicated from AdminTravelToolsWidgets to keep modules independent) ───
const SectionCard = ({ icon, title, hint, right, children }) => (
  <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
    <div className="px-5 py-4 bg-gradient-to-r from-gray-800 to-gray-700 border-b border-gray-700 flex items-center justify-between gap-3">
      <div className="flex items-center gap-3 min-w-0">
        <span className="text-3xl">{icon}</span>
        <div className="min-w-0">
          <h3 className="text-lg font-bold text-white truncate">{title}</h3>
          {hint && <p className="text-xs text-gray-400 mt-0.5">{hint}</p>}
        </div>
      </div>
      {right}
    </div>
    <div className="p-5 space-y-4">{children}</div>
  </div>
);

const Field = ({ label, hint, children }) => (
  <div>
    <label className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">{label}</label>
    {children}
    {hint && <p className="text-[11px] text-gray-500 mt-1">{hint}</p>}
  </div>
);

const TextInput = (props) => (
  <input
    type="text"
    {...props}
    className={`w-full px-3 py-2 rounded-lg bg-gray-700 border border-gray-600 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${props.className || ''}`}
  />
);

const NumberInput = (props) => (
  <input
    type="number"
    {...props}
    className={`w-full px-3 py-2 rounded-lg bg-gray-700 border border-gray-600 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${props.className || ''}`}
  />
);

// ─── Transport types: stored value <-> friendly label ────────────────────────
// `taxi` is the legacy stored value; we surface it as "Car / Taxi" so the user
// thinks in terms of cars (per request "train bus car"). No data migration.
const TRANSPORT_TYPES = [
  { value: 'train', icon: '🚆', label: 'Train' },
  { value: 'bus',   icon: '🚌', label: 'Bus' },
  { value: 'taxi',  icon: '🚗', label: 'Car / Taxi' },
];
const typeMeta = (value) => TRANSPORT_TYPES.find(t => t.value === value) || { icon: '❓', label: value || 'Other' };

// ─── ONCF rail network — cities where train autofill is allowed ──────────────
// Source: Wikipedia ONCF article. If both ends of a route are here, the auto-fill
// will offer a train entry; otherwise it leaves train alone.
const ONCF_RAIL = new Set([
  'tangier', 'tanger', 'kenitra', 'kénitra', 'sidi kacem', 'sidi kasim',
  'rabat', 'sale', 'salé', 'mohammedia', 'casablanca', 'casa', 'settat',
  'el jadida', 'marrakech', 'marrakesh', 'meknes', 'meknès', 'fes', 'fez', 'fès',
  'taza', 'taourirt', 'oujda', 'nador', 'asilah',
]);

const isRailCity = (name) => ONCF_RAIL.has((name || '').toLowerCase().trim());

const _fmtDuration = (mins) => {
  if (!mins && mins !== 0) return '—';
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
};

const kmToMi = (km) => (typeof km === 'number' && !isNaN(km) ? Math.round(km * 0.621371) : null);

const AdminTravelToolsDistance = ({ API, authHeaders, onMessage }) => {
  // Header sub-tab — local state, not persisted (matches user's "tabs" wording).
  const [activeSection, setActiveSection] = useState('routes'); // 'cities' | 'routes' | 'settings'

  // Data
  const [cities, setCities] = useState([]);                     // transport_cities — what routes reference (names only)
  const [routes, setRoutes] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [markerCities, setMarkerCities] = useState([]);         // traveltools_cities — has lat/lng, used ONLY for OSRM lookup

  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null); // id of in-flight save, or 'new-city' / 'new-route' / 'pricing' / 'settings' / 'autofill-all'
  const [autofillProgress, setAutofillProgress] = useState(null); // { done, total } during bulk autofill
  const [search, setSearch] = useState('');
  const [editingRoute, setEditingRoute] = useState(null); // route object (existing) or { __new: true } draft
  const [newCity, setNewCity] = useState({ name: '', name_ar: '', name_fr: '' });
  // Distance unit preference — stored in transport_settings under a separate doc.
  const [defaultUnit, setDefaultUnit] = useState('km');
  // Import-from-Buddy modal: null | 'confirm' | 'progress' | { ok, data } | { ok:false, err }
  const [importModal, setImportModal] = useState(null);

  const toast = (msg) => { if (typeof onMessage === 'function') onMessage(msg); };

  const adminAuth = authHeaders || {};
  // Strip Content-Type if caller pre-set it — we use FormData, browser must set its own boundary.
  const formAuth = { ...adminAuth };
  delete formAuth['Content-Type'];
  delete formAuth['content-type'];

  // ── Initial load ─────────────────────────────────────────────────────────
  useEffect(() => {
    let aborted = false;
    (async () => {
      setLoading(true); setError(null);
      try {
        // markerCitiesRes is the traveltools_cities collection (with lat/lng). PUBLIC endpoint —
        // no admin headers needed. We use it ONLY to look up coordinates for OSRM during autofill;
        // we never modify it from this tab. The admin Cities sub-tab in THIS file edits a different
        // collection (transport_cities) that has names only.
        const [citiesRes, routesRes, pricingRes, markerCitiesRes] = await Promise.all([
          fetch(`${API}/api/travel-tools/admin/distance/cities`, { headers: adminAuth }),
          fetch(`${API}/api/travel-tools/admin/distance/routes`, { headers: adminAuth }),
          fetch(`${API}/api/travel-tools/admin/distance/pricing-formula`, { headers: adminAuth }),
          fetch(`${API}/api/travel-tools/cities`),
        ]);
        if (!citiesRes.ok) throw new Error(`Cities HTTP ${citiesRes.status}`);
        if (!routesRes.ok) throw new Error(`Routes HTTP ${routesRes.status}`);
        const citiesData = await citiesRes.json();
        const routesData = await routesRes.json();
        const pricingData = pricingRes.ok ? await pricingRes.json() : null;
        const markerCitiesData = markerCitiesRes.ok ? await markerCitiesRes.json() : [];
        if (aborted) return;
        setCities(citiesData.cities || []);
        setRoutes(routesData.routes || []);
        setPricing(pricingData || null);
        // /api/travel-tools/cities returns either an array directly (seed path) or an object — handle both.
        const markerList = Array.isArray(markerCitiesData) ? markerCitiesData : (markerCitiesData.cities || []);
        setMarkerCities(markerList);
        // Default unit lives in pricing/settings doc — fallback 'km'
        if (pricingData && pricingData.default_unit) setDefaultUnit(pricingData.default_unit);
      } catch (e) {
        if (!aborted) setError(String(e.message || e));
      } finally {
        if (!aborted) setLoading(false);
      }
    })();
    return () => { aborted = true; };
  }, [API, adminAuth]);

  // ── Cities CRUD ──────────────────────────────────────────────────────────
  const addCity = async () => {
    const name = newCity.name.trim();
    if (!name) { toast('City name required'); return; }
    setBusy('new-city');
    try {
      const fd = new FormData();
      fd.append('name', name);
      fd.append('name_ar', newCity.name_ar.trim());
      fd.append('name_fr', newCity.name_fr.trim());
      const res = await fetch(`${API}/api/travel-tools/admin/distance/cities`, {
        method: 'POST', headers: formAuth, body: fd
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setCities(prev => [...prev, data.city]);
      setNewCity({ name: '', name_ar: '', name_fr: '' });
      toast(`City "${name}" added`);
    } catch (e) {
      toast(`Add city failed: ${e.message || e}`);
    } finally { setBusy(null); }
  };

  const deleteCity = async (city) => {
    if (!window.confirm(`Delete "${city.name}"?\n\nWarning: routes using this city will keep working but show the stored name as a string.`)) return;
    setBusy(`city-${city.id}`);
    try {
      const res = await fetch(`${API}/api/travel-tools/admin/distance/cities/${city.id}`, {
        method: 'DELETE', headers: adminAuth
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setCities(prev => prev.filter(c => c.id !== city.id));
      toast(`City "${city.name}" deleted`);
    } catch (e) {
      toast(`Delete failed: ${e.message || e}`);
    } finally { setBusy(null); }
  };

  // ── Routes CRUD ──────────────────────────────────────────────────────────
  const startNewRoute = () => {
    setEditingRoute({
      __new: true,
      from_city: '',
      to_city: '',
      is_active: true,
      distance_km: '',
      transports: [
        { type: 'train', is_best: true, direct: true, time_minutes: '', operator: 'ONCF', station_from: '', station_to: '', frequency_per_day: '', tips: [] }
      ],
    });
  };

  const saveRoute = async (route) => {
    if (!route.from_city || !route.to_city) { toast('From and To cities required'); return; }
    if (route.from_city === route.to_city) { toast('From and To must differ'); return; }
    setBusy(route.__new ? 'new-route' : `route-${route.id}`);
    try {
      const fd = new FormData();
      fd.append('from_city', route.from_city);
      fd.append('to_city', route.to_city);
      // Clean transports: drop empties, coerce numbers
      const cleanTransports = (route.transports || []).map(t => ({
        type: t.type,
        is_best: !!t.is_best,
        direct: t.direct !== false, // default true
        time_minutes: parseInt(t.time_minutes, 10) || 0,
        operator: t.operator || '',
        station_from: t.station_from || '',
        station_to: t.station_to || '',
        frequency_per_day: parseInt(t.frequency_per_day, 10) || 0,
        tips: Array.isArray(t.tips) ? t.tips.filter(Boolean) : [],
      }));
      fd.append('transports', JSON.stringify(cleanTransports));
      if (route.distance_km !== '' && route.distance_km !== null && route.distance_km !== undefined) {
        const km = parseFloat(route.distance_km);
        if (!isNaN(km)) fd.append('distance_km', String(km));
      }
      const url = route.__new
        ? `${API}/api/travel-tools/admin/distance/routes`
        : `${API}/api/travel-tools/admin/distance/routes/${route.id}`;
      const method = route.__new ? 'POST' : 'PUT';
      if (!route.__new) fd.append('is_active', String(!!route.is_active));
      const res = await fetch(url, { method, headers: formAuth, body: fd });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      // Re-fetch routes — backend doesn't return the full updated doc on PUT
      const refresh = await fetch(`${API}/api/travel-tools/admin/distance/routes`, { headers: adminAuth });
      if (refresh.ok) {
        const data = await refresh.json();
        setRoutes(data.routes || []);
      }
      setEditingRoute(null);
      toast(route.__new ? 'Route created' : 'Route updated');
    } catch (e) {
      toast(`Save failed: ${e.message || e}`);
    } finally { setBusy(null); }
  };

  const deleteRoute = async (route) => {
    if (!window.confirm(`Delete route ${route.from_city} → ${route.to_city}?`)) return;
    setBusy(`route-${route.id}`);
    try {
      const res = await fetch(`${API}/api/travel-tools/admin/distance/routes/${route.id}`, {
        method: 'DELETE', headers: adminAuth
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setRoutes(prev => prev.filter(r => r.id !== route.id));
      toast('Route deleted');
    } catch (e) {
      toast(`Delete failed: ${e.message || e}`);
    } finally { setBusy(null); }
  };

  // ── Settings (distance unit + pricing) ───────────────────────────────────
  const savePricing = async (overrides = {}) => {
    setBusy('pricing');
    try {
      const fd = new FormData();
      const next = { ...(pricing || {}), ...overrides };
      if (next.title !== undefined) fd.append('title', next.title || '');
      if (next.formula_text !== undefined) fd.append('formula_text', next.formula_text || '');
      if (next.disclaimer !== undefined) fd.append('disclaimer', next.disclaimer || '');
      // default_unit is OUR addition — the backend just $set's whatever we send,
      // so persisting it through the same endpoint keeps it in the same doc.
      if (next.default_unit !== undefined) fd.append('default_unit', next.default_unit);
      if (Array.isArray(next.transports)) fd.append('transports', JSON.stringify(next.transports));
      const res = await fetch(`${API}/api/travel-tools/admin/distance/pricing-formula`, {
        method: 'PUT', headers: formAuth, body: fd
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      // Refresh
      const refresh = await fetch(`${API}/api/travel-tools/admin/distance/pricing-formula`, { headers: adminAuth });
      if (refresh.ok) {
        const data = await refresh.json();
        setPricing(data || null);
        if (data && data.default_unit) setDefaultUnit(data.default_unit);
      }
      toast('Settings saved');
    } catch (e) {
      toast(`Save failed: ${e.message || e}`);
    } finally { setBusy(null); }
  };

  // ── One-shot import from Darija Buddy's transport_* collections ───────────
  // Safe to run multiple times — skips any docs whose id is already in the new collections.
  const runImportFromBuddy = async () => {
    setImportModal('progress');
    setBusy('import');
    try {
      const res = await fetch(`${API}/api/travel-tools/admin/distance/migrate-from-buddy`, {
        method: 'POST', headers: adminAuth
      });
      if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try { const j = await res.json(); if (j?.detail) detail += ` — ${j.detail}`; } catch (_) {}
        throw new Error(detail);
      }
      const data = await res.json();
      // Refresh all three lists
      const [c, r, p] = await Promise.all([
        fetch(`${API}/api/travel-tools/admin/distance/cities`, { headers: adminAuth }),
        fetch(`${API}/api/travel-tools/admin/distance/routes`, { headers: adminAuth }),
        fetch(`${API}/api/travel-tools/admin/distance/pricing-formula`, { headers: adminAuth }),
      ]);
      if (c.ok) { const d = await c.json(); setCities(d.cities || []); }
      if (r.ok) { const d = await r.json(); setRoutes(d.routes || []); }
      if (p.ok) { const d = await p.json(); setPricing(d || null); if (d?.default_unit) setDefaultUnit(d.default_unit); }
      setImportModal({ ok: true, data });
    } catch (e) {
      setImportModal({ ok: false, err: e.message || String(e) });
    } finally { setBusy(null); }
  };

  const importFromBuddy = () => setImportModal('confirm');

  // ── Seed default Moroccan cities (~50) into traveltools_distance_cities ────
  const seedMoroccoCities = async () => {
    if (!window.confirm('Add ~50 default Moroccan cities to the Distance tab?\n\nSkips any city you already have (matched by name). Safe to click more than once.')) return;
    setBusy('seed-cities');
    try {
      const res = await fetch(`${API}/api/travel-tools/admin/distance/seed-cities`, {
        method: 'POST', headers: adminAuth
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      // Refresh cities
      const refresh = await fetch(`${API}/api/travel-tools/admin/distance/cities`, { headers: adminAuth });
      if (refresh.ok) {
        const d = await refresh.json();
        setCities(d.cities || []);
      }
      toast(`✓ Added ${data.added} cities · skipped ${data.skipped} duplicates`);
    } catch (e) {
      toast(`Seed failed: ${e.message || e}`);
    } finally { setBusy(null); }
  };

  // ── Marker-cities lookup (name → {lat, lng}) for OSRM autofill ────────────
  // Source: traveltools_cities (the map markers list). We match by case-insensitive name.
  const cityCoordMap = useMemo(() => {
    const m = new Map();
    for (const c of (markerCities || [])) {
      const name = (c.name || '').toLowerCase().trim();
      if (!name) continue;
      const lat = typeof c.lat === 'number' ? c.lat : parseFloat(c.lat);
      const lng = typeof c.lng === 'number' ? c.lng : parseFloat(c.lng);
      if (isNaN(lat) || isNaN(lng)) continue;
      m.set(name, { lat, lng, displayName: c.name });
    }
    return m;
  }, [markerCities]);

  // ── Decide whether a route is "complete" enough to skip during bulk autofill.
  // Complete = has a distance AND every existing transport mode already has a time.
  const isRouteComplete = (r) => {
    if (!r.distance_km || r.distance_km <= 0) return false;
    const ts = r.transports || [];
    if (ts.length === 0) return false; // no modes → needs filling
    for (const t of ts) {
      if (!t.time_minutes || t.time_minutes <= 0) return false;
    }
    return true;
  };

  // ── Single OSRM call → { km, carMin } or throws.
  const callOSRM = async (fromCoord, toCoord) => {
    const url = `https://router.project-osrm.org/route/v1/driving/${fromCoord.lng},${fromCoord.lat};${toCoord.lng},${toCoord.lat}?overview=false`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`OSRM HTTP ${res.status}`);
    const data = await res.json();
    if (data.code !== 'Ok' || !data.routes || !data.routes[0]) throw new Error(`OSRM: ${data.code || 'no route'}`);
    return {
      km: Math.round(data.routes[0].distance / 1000),
      carMin: Math.round(data.routes[0].duration / 60),
    };
  };

  // ── Build the autofilled route object (does NOT save). Returns { next, filled[] }.
  // Throws if either city is missing coords.
  const computeAutofill = async (route) => {
    const fromCoord = cityCoordMap.get((route.from_city || '').toLowerCase().trim());
    const toCoord = cityCoordMap.get((route.to_city || '').toLowerCase().trim());
    const missing = [];
    if (!fromCoord) missing.push(route.from_city || '(empty)');
    if (!toCoord) missing.push(route.to_city || '(empty)');
    if (missing.length) {
      throw new Error(`Add to Travel Tools → Cities: ${missing.join(', ')}`);
    }
    const { km, carMin } = await callOSRM(fromCoord, toCoord);
    const filled = [];

    const next = { ...route };
    if (!next.distance_km || next.distance_km <= 0) {
      next.distance_km = km;
      filled.push('distance');
    }

    const transports = (next.transports || []).map(t => ({ ...t }));

    // Helper to set or create a transport mode
    const ensureMode = (type, defaults) => {
      const idx = transports.findIndex(t => t.type === type);
      if (idx === -1) {
        transports.push({
          type, is_best: defaults.is_best || false, direct: true,
          time_minutes: defaults.time_minutes,
          operator: defaults.operator || '', station_from: '', station_to: '',
          frequency_per_day: 0, tips: [],
        });
        filled.push(`+${type}`);
      } else if (!transports[idx].time_minutes || transports[idx].time_minutes <= 0) {
        transports[idx] = { ...transports[idx], time_minutes: defaults.time_minutes };
        filled.push(`${type}-time`);
      }
    };

    // Car / Taxi — always
    ensureMode('taxi', { time_minutes: carMin, operator: '' });
    // Bus — always (CTM/Supratours covers all Morocco intercity)
    ensureMode('bus', { time_minutes: Math.round(carMin * 1.5), operator: 'CTM / Supratours' });
    // Train — only if both cities are on the ONCF rail network
    if (isRailCity(route.from_city) && isRailCity(route.to_city)) {
      ensureMode('train', { time_minutes: Math.round(carMin * 0.9), operator: 'ONCF', is_best: true });
    }

    next.transports = transports;
    return { next, filled };
  };

  // ── Save a route silently (for bulk fill) — no toast, no refresh between saves.
  const saveRouteSilent = async (route) => {
    const fd = new FormData();
    fd.append('from_city', route.from_city);
    fd.append('to_city', route.to_city);
    const cleanTransports = (route.transports || []).map(t => ({
      type: t.type,
      is_best: !!t.is_best,
      direct: t.direct !== false,
      time_minutes: parseInt(t.time_minutes, 10) || 0,
      operator: t.operator || '',
      station_from: t.station_from || '',
      station_to: t.station_to || '',
      frequency_per_day: parseInt(t.frequency_per_day, 10) || 0,
      tips: Array.isArray(t.tips) ? t.tips.filter(Boolean) : [],
    }));
    fd.append('transports', JSON.stringify(cleanTransports));
    if (route.distance_km !== '' && route.distance_km !== null && route.distance_km !== undefined) {
      const km = parseFloat(route.distance_km);
      if (!isNaN(km)) fd.append('distance_km', String(km));
    }
    fd.append('is_active', String(route.is_active !== false));
    const res = await fetch(`${API}/api/travel-tools/admin/distance/routes/${route.id}`, {
      method: 'PUT', headers: formAuth, body: fd,
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
  };

  // ── Autofill ONE route (with toast).
  const autofillOne = async (route) => {
    setBusy(`route-${route.id}`);
    try {
      const { next, filled } = await computeAutofill(route);
      if (filled.length === 0) {
        toast(`${route.from_city} → ${route.to_city}: already complete`);
        return;
      }
      await saveRouteSilent(next);
      // Refresh list so the row updates in place
      const refresh = await fetch(`${API}/api/travel-tools/admin/distance/routes`, { headers: adminAuth });
      if (refresh.ok) {
        const data = await refresh.json();
        setRoutes(data.routes || []);
      }
      toast(`✓ ${route.from_city} → ${route.to_city}: ${filled.join(', ')}`);
    } catch (e) {
      toast(`✗ ${route.from_city} → ${route.to_city}: ${e.message || e}`);
    } finally {
      setBusy(null);
    }
  };

  // ── Autofill ALL routes that are incomplete (bulk).
  const autofillAll = async () => {
    const todo = routes.filter(r => !isRouteComplete(r));
    if (todo.length === 0) { toast('All routes already complete — nothing to fill'); return; }
    if (!window.confirm(`Auto-fill ${todo.length} route(s)?\n\nThis calls the OSRM public router (free) once per route with a 300ms delay. Existing values are never overwritten.`)) return;
    setBusy('autofill-all');
    setAutofillProgress({ done: 0, total: todo.length });
    let filledCount = 0;
    const errors = [];
    for (let i = 0; i < todo.length; i++) {
      const r = todo[i];
      try {
        const { next, filled } = await computeAutofill(r);
        if (filled.length > 0) {
          await saveRouteSilent(next);
          filledCount++;
        }
      } catch (e) {
        errors.push(`${r.from_city}→${r.to_city}: ${e.message || e}`);
      }
      setAutofillProgress({ done: i + 1, total: todo.length });
      // Polite delay between OSRM calls (public router has soft rate limits).
      if (i < todo.length - 1) await new Promise(res => setTimeout(res, 300));
    }
    // One refresh at the end
    try {
      const refresh = await fetch(`${API}/api/travel-tools/admin/distance/routes`, { headers: adminAuth });
      if (refresh.ok) {
        const data = await refresh.json();
        setRoutes(data.routes || []);
      }
    } catch {}
    setAutofillProgress(null);
    setBusy(null);
    let msg = `Filled ${filledCount} of ${todo.length}`;
    if (errors.length > 0) {
      const shown = errors.slice(0, 3).join(' · ');
      msg += ` · ${errors.length} skipped: ${shown}${errors.length > 3 ? '…' : ''}`;
    }
    toast(msg);
  };

  // ── Filtered routes (search) ─────────────────────────────────────────────
  const filteredRoutes = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return routes;
    return routes.filter(r =>
      (r.from_city || '').toLowerCase().includes(q) ||
      (r.to_city || '').toLowerCase().includes(q)
    );
  }, [routes, search]);

  // ── Stats ────────────────────────────────────────────────────────────────
  const stats = useMemo(() => ({
    cities: cities.length,
    routes: routes.length,
    active: routes.filter(r => r.is_active !== false).length,
  }), [cities, routes]);

  if (loading) {
    return <div className="bg-gray-800 rounded-xl p-12 text-center text-gray-400">Loading distance data…</div>;
  }
  if (error) {
    return (
      <div className="bg-red-900/30 border border-red-700 rounded-xl p-6 text-red-200">
        <div className="font-bold mb-2">Could not load distance data</div>
        <div className="text-sm">{error}</div>
        <div className="text-xs text-red-300 mt-2">Make sure the backend has been restarted (the route endpoints now accept distance_km).</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Intro + sub-tabs */}
      <div className="bg-gradient-to-r from-indigo-900/40 to-blue-900/40 rounded-xl p-6 border border-indigo-700/50">
        <div className="flex items-center gap-3 mb-3">
          <span className="text-4xl">📏</span>
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-white">Distance &amp; Routes</h2>
            <p className="text-gray-300 text-sm">
              Manage cities, routes between them, transport modes (train · bus · car), distances and times.
              Independent from Darija Buddy — edits here do not affect Buddy.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-4 mt-2">
          <div className="text-xs text-gray-300 flex items-center gap-3 bg-gray-900/40 px-3 py-1.5 rounded-lg">
            <span><b className="text-white">{stats.cities}</b> cities</span>
            <span className="opacity-40">·</span>
            <span><b className="text-white">{stats.routes}</b> routes</span>
            <span className="opacity-40">·</span>
            <span><b className="text-green-400">{stats.active}</b> active</span>
          </div>
        </div>
        <div className="flex gap-2 mt-4 flex-wrap items-center">
          {[
            { id: 'cities', label: '🏙️ Cities' },
            { id: 'routes', label: '🛣️ Routes' },
            { id: 'settings', label: '⚙️ Settings' },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setActiveSection(t.id)}
              className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${
                activeSection === t.id
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              {t.label}
            </button>
          ))}
          {(cities.length === 0 && routes.length === 0) && (
            <button
              onClick={importFromBuddy}
              disabled={busy === 'import'}
              className="ml-auto px-4 py-2 rounded-lg text-sm font-bold bg-amber-600 hover:bg-amber-500 text-white disabled:opacity-50"
              title="One-shot copy from Darija Buddy's transport data into Travel Tools' own collections"
            >
              {busy === 'import' ? 'Importing…' : '📥 Import from Buddy'}
            </button>
          )}
        </div>
      </div>

      {/* ───────────── CITIES ───────────── */}
      {activeSection === 'cities' && (
        <SectionCard icon="🏙️" title="Cities" hint="The list of city names that can be used as From / To in routes.">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
            <Field label="Name (English)">
              <TextInput value={newCity.name} onChange={(e) => setNewCity({ ...newCity, name: e.target.value })} placeholder="Casablanca" />
            </Field>
            <Field label="Name (Arabic, optional)">
              <TextInput value={newCity.name_ar} onChange={(e) => setNewCity({ ...newCity, name_ar: e.target.value })} placeholder="الدار البيضاء" />
            </Field>
            <Field label="Name (French, optional)">
              <TextInput value={newCity.name_fr} onChange={(e) => setNewCity({ ...newCity, name_fr: e.target.value })} placeholder="Casablanca" />
            </Field>
            <div className="flex items-end">
              <button
                onClick={addCity}
                disabled={busy === 'new-city' || !newCity.name.trim()}
                className="w-full px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-bold"
              >
                {busy === 'new-city' ? 'Adding…' : '+ Add City'}
              </button>
            </div>
          </div>

          {cities.length === 0 ? (
            <div className="text-center text-gray-500 py-8 text-sm">No cities yet. Add the first one above.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
              {cities.slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(c => (
                <div key={c.id} className="bg-gray-900/60 border border-gray-700 rounded-lg p-3 flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-bold text-white truncate">{c.name}</div>
                    <div className="text-[11px] text-gray-400 truncate">{c.name_ar || '—'} · {c.name_fr || '—'}</div>
                  </div>
                  <button
                    onClick={() => deleteCity(c)}
                    disabled={busy === `city-${c.id}`}
                    className="shrink-0 px-2 py-1 rounded bg-red-900/50 hover:bg-red-800 text-red-200 text-xs"
                    title="Delete city"
                  >
                    {busy === `city-${c.id}` ? '…' : '🗑️'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      )}

      {/* ───────────── ROUTES ───────────── */}
      {activeSection === 'routes' && (
        <SectionCard
          icon="🛣️"
          title="Routes"
          hint={`${filteredRoutes.length} of ${routes.length} shown. Search filters by either city.`}
          right={
            <div className="flex gap-2">
              <button
                onClick={autofillAll}
                disabled={busy === 'autofill-all' || markerCities.length === 0}
                title={markerCities.length === 0 ? 'No marker cities loaded — open Travel Tools → Cities first' : 'Auto-fill distance + times for all routes missing data, using OSRM'}
                className="shrink-0 px-3 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white text-sm font-bold whitespace-nowrap"
              >
                {busy === 'autofill-all' && autofillProgress
                  ? `🤖 Filling ${autofillProgress.done}/${autofillProgress.total}…`
                  : '🤖 Auto-fill all missing'}
              </button>
              <button
                onClick={startNewRoute}
                className="shrink-0 px-3 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-bold whitespace-nowrap"
              >
                + Add Route
              </button>
            </div>
          }
        >
          {/* Search */}
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="🔍 Search by city (e.g. Casa, Marrakech…)"
            className="w-full px-3 py-2 rounded-lg bg-gray-700 border border-gray-600 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />

          {/* Editor card */}
          {editingRoute && (
            <RouteEditor
              route={editingRoute}
              cities={cities}
              onChange={setEditingRoute}
              onSave={() => saveRoute(editingRoute)}
              onCancel={() => setEditingRoute(null)}
              busy={busy === 'new-route' || busy === `route-${editingRoute.id}`}
            />
          )}

          {/* List */}
          {filteredRoutes.length === 0 ? (
            <div className="text-center text-gray-500 py-8 text-sm">
              {routes.length === 0 ? 'No routes yet. Click + Add Route to create one.' : 'No routes match your search.'}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredRoutes
                .slice()
                .sort((a, b) => (a.from_city || '').localeCompare(b.from_city || '') || (a.to_city || '').localeCompare(b.to_city || ''))
                .map(r => (
                <RouteRow
                  key={r.id}
                  route={r}
                  onEdit={() => setEditingRoute({ ...r })}
                  onDelete={() => deleteRoute(r)}
                  onAutofill={() => autofillOne(r)}
                  canAutofill={cityCoordMap.size > 0}
                  busy={busy === `route-${r.id}`}
                  unit={defaultUnit}
                  isComplete={isRouteComplete(r)}
                />
              ))}
            </div>
          )}
        </SectionCard>
      )}

      {/* ───────────── SETTINGS ───────────── */}
      {activeSection === 'settings' && (
        <SectionCard icon="⚙️" title="Settings" hint="Defaults that apply across all routes.">
          <Field label="Default distance unit" hint="The user-facing route card shows both km and mi; this controls which one is primary.">
            <div className="flex gap-2">
              {['km', 'mi'].map(u => (
                <button
                  key={u}
                  onClick={() => { setDefaultUnit(u); savePricing({ default_unit: u }); }}
                  disabled={busy === 'pricing'}
                  className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${
                    defaultUnit === u ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  {u.toUpperCase()}
                </button>
              ))}
            </div>
          </Field>
          <Field label="Pricing / disclaimer text" hint="Optional note about pricing. Shown in the Darija Buddy chat search results.">
            <TextInput
              value={(pricing && pricing.disclaimer) || ''}
              onChange={(e) => setPricing({ ...(pricing || {}), disclaimer: e.target.value })}
              placeholder="Prices may vary by season and demand."
            />
          </Field>
          <button
            onClick={() => savePricing({ disclaimer: (pricing && pricing.disclaimer) || '' })}
            disabled={busy === 'pricing'}
            className="px-4 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-bold"
          >
            {busy === 'pricing' ? 'Saving…' : '💾 Save settings'}
          </button>
        </SectionCard>
      )}

      {/* ───────────── Import-from-Buddy modal ───────────── */}
      {importModal !== null && (
        <div
          className="fixed inset-0 z-[9999] bg-black/70 flex items-center justify-center p-4"
          onClick={() => { if (importModal !== 'progress') setImportModal(null); }}
        >
          <div
            className="bg-gray-900 rounded-2xl border border-gray-700 shadow-2xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            {importModal === 'confirm' && (
              <>
                <div className="text-3xl mb-3">📥</div>
                <h3 className="text-xl font-bold text-white mb-2">Import from Darija Buddy</h3>
                <p className="text-sm text-gray-300 mb-4 leading-relaxed">
                  Copy all <b className="text-white">Cities</b>, <b className="text-white">Routes</b>, and{' '}
                  <b className="text-white">Settings</b> from the Darija Buddy Transport tab into Travel Tools
                  Distance.
                </p>
                <div className="bg-gray-800/60 border border-gray-700 rounded-lg p-3 text-xs text-gray-300 mb-5 space-y-1">
                  <div>✓ Buddy stays completely untouched</div>
                  <div>✓ Safe to run more than once — skips duplicates</div>
                  <div>✓ One-time copy, takes a few seconds</div>
                </div>
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={() => setImportModal(null)}
                    className="px-4 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-white text-sm font-bold"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={runImportFromBuddy}
                    className="px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-500 text-white text-sm font-bold"
                  >
                    📥 Import now
                  </button>
                </div>
              </>
            )}

            {importModal === 'progress' && (
              <div className="text-center py-4">
                <div className="text-4xl mb-3 animate-pulse">📥</div>
                <h3 className="text-lg font-bold text-white mb-1">Importing…</h3>
                <p className="text-sm text-gray-400">Copying cities, routes, and settings from Buddy.</p>
              </div>
            )}

            {importModal && typeof importModal === 'object' && importModal.ok === true && (
              <>
                <div className="text-4xl mb-3">✅</div>
                <h3 className="text-xl font-bold text-white mb-2">Import complete</h3>
                <div className="bg-gray-800/60 border border-gray-700 rounded-lg p-4 text-sm text-gray-200 space-y-1 mb-5">
                  <div className="flex justify-between"><span className="text-gray-400">Cities copied</span><b>{importModal.data.cities_copied}</b></div>
                  <div className="flex justify-between"><span className="text-gray-400">Routes copied</span><b>{importModal.data.routes_copied}</b></div>
                  <div className="flex justify-between"><span className="text-gray-400">Settings copied</span><b>{importModal.data.settings_copied}</b></div>
                </div>
                <p className="text-xs text-gray-400 mb-4">
                  Travel Tools Distance now has its OWN copy. Edits here will NOT affect Darija Buddy.
                </p>
                <div className="flex justify-end">
                  <button
                    onClick={() => setImportModal(null)}
                    className="px-4 py-2 rounded-lg bg-green-600 hover:bg-green-500 text-white text-sm font-bold"
                  >
                    Done
                  </button>
                </div>
              </>
            )}

            {importModal && typeof importModal === 'object' && importModal.ok === false && (
              <>
                <div className="text-4xl mb-3">⚠️</div>
                <h3 className="text-xl font-bold text-white mb-2">Import failed</h3>
                <p className="text-sm text-gray-300 mb-3">Something went wrong:</p>
                <div className="bg-red-950/40 border border-red-800 rounded-lg p-3 text-xs text-red-200 font-mono mb-5 break-words">
                  {importModal.err}
                </div>
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={() => setImportModal(null)}
                    className="px-4 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-white text-sm font-bold"
                  >
                    Close
                  </button>
                  <button
                    onClick={runImportFromBuddy}
                    className="px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-500 text-white text-sm font-bold"
                  >
                    Retry
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Route row (collapsed display in the list) ────────────────────────────
const RouteRow = ({ route, onEdit, onDelete, onAutofill, canAutofill, busy, unit, isComplete }) => {
  const km = typeof route.distance_km === 'number' ? route.distance_km : null;
  const mi = km !== null ? kmToMi(km) : null;
  // Best (or shortest) per type for the summary chip strip
  const byType = (t) => {
    const list = (route.transports || []).filter(x => x.type === t);
    if (list.length === 0) return null;
    return list.reduce((a, b) => (a.time_minutes < b.time_minutes ? a : b));
  };
  const chips = TRANSPORT_TYPES.map(t => {
    const entry = byType(t.value);
    return { ...t, entry };
  });

  return (
    <div className="bg-gray-900/60 border border-gray-700 rounded-lg p-3 flex flex-wrap items-center gap-3">
      <div className="min-w-0 flex-1">
        <div className="text-sm font-bold text-white truncate">
          {route.from_city} <span className="opacity-50">→</span> {route.to_city}
          {route.is_active === false && <span className="ml-2 px-1.5 py-0.5 rounded bg-red-900/50 text-red-300 text-[10px] font-bold uppercase">Inactive</span>}
        </div>
        <div className="text-[11px] text-gray-400 flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
          {km !== null ? (
            <span>{unit === 'mi' ? `${mi} mi · ${km} km` : `${km} km · ${mi} mi`}</span>
          ) : (
            <span className="opacity-50">no distance set</span>
          )}
          {chips.filter(c => c.entry).map(c => (
            <span key={c.value} className="inline-flex items-center gap-1">
              <span>{c.icon}</span>
              <span>{_fmtDuration(c.entry.time_minutes)}</span>
            </span>
          ))}
        </div>
      </div>
      <div className="flex gap-1 shrink-0">
        <button
          onClick={onAutofill}
          disabled={busy || !canAutofill || isComplete}
          title={
            !canAutofill ? 'No marker cities loaded'
            : isComplete ? 'Already complete — nothing to fill (use ✏️ Edit to change)'
            : 'Auto-fill distance + times via OSRM (only fills empty fields)'
          }
          className={`px-2.5 py-1.5 rounded text-white text-xs font-bold ${isComplete ? 'bg-gray-700 opacity-50 cursor-not-allowed' : 'bg-amber-600 hover:bg-amber-700'}`}
        >
          🤖
        </button>
        <button onClick={onEdit} disabled={busy} className="px-2.5 py-1.5 rounded bg-blue-700/60 hover:bg-blue-700 text-white text-xs font-bold">✏️ Edit</button>
        <button onClick={onDelete} disabled={busy} className="px-2.5 py-1.5 rounded bg-red-900/50 hover:bg-red-800 text-red-200 text-xs font-bold">🗑️</button>
      </div>
    </div>
  );
};

// ─── Inline route editor (used for new + edit) ────────────────────────────
const RouteEditor = ({ route, cities, onChange, onSave, onCancel, busy }) => {
  const patch = (partial) => onChange({ ...route, ...partial });
  const patchTransport = (idx, partial) => {
    const next = (route.transports || []).slice();
    next[idx] = { ...next[idx], ...partial };
    onChange({ ...route, transports: next });
  };
  const addTransport = (type) => {
    const next = [...(route.transports || []), {
      type, is_best: false, direct: true, time_minutes: '', operator: '', station_from: '', station_to: '', frequency_per_day: '', tips: []
    }];
    onChange({ ...route, transports: next });
  };
  const removeTransport = (idx) => {
    const next = (route.transports || []).slice();
    next.splice(idx, 1);
    onChange({ ...route, transports: next });
  };
  const km = parseFloat(route.distance_km);
  const mi = !isNaN(km) ? kmToMi(km) : null;

  const cityNames = cities.slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(c => c.name);

  return (
    <div className="bg-gray-900/80 border-2 border-blue-600/60 rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-bold text-blue-300">{route.__new ? 'New route' : `Editing: ${route.from_city} → ${route.to_city}`}</div>
        <button onClick={onCancel} className="text-gray-400 hover:text-white text-sm">✕</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Field label="From">
          <select
            value={route.from_city || ''}
            onChange={(e) => patch({ from_city: e.target.value })}
            className="w-full px-3 py-2 rounded-lg bg-gray-700 border border-gray-600 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">— pick a city —</option>
            {cityNames.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </Field>
        <Field label="To">
          <select
            value={route.to_city || ''}
            onChange={(e) => patch({ to_city: e.target.value })}
            className="w-full px-3 py-2 rounded-lg bg-gray-700 border border-gray-600 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">— pick a city —</option>
            {cityNames.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </Field>
        <Field label="Distance (km)" hint={mi !== null ? `= ${mi} mi` : 'Optional. Auto-shows miles.'}>
          <NumberInput
            value={route.distance_km ?? ''}
            onChange={(e) => patch({ distance_km: e.target.value })}
            min={0}
            step={1}
            placeholder="e.g. 240"
          />
        </Field>
      </div>

      {!route.__new && (
        <Field label="Active">
          <label className="inline-flex items-center gap-2 text-sm text-gray-200">
            <input
              type="checkbox"
              checked={route.is_active !== false}
              onChange={(e) => patch({ is_active: e.target.checked })}
            />
            <span>Show this route on the map &amp; in search results</span>
          </label>
        </Field>
      )}

      {/* Transports */}
      <div className="space-y-2">
        <div className="text-xs font-bold uppercase tracking-wider text-gray-400">Transport modes</div>
        {(route.transports || []).length === 0 && (
          <div className="text-xs text-gray-500 italic">No modes yet — add at least one below.</div>
        )}
        {(route.transports || []).map((t, idx) => {
          const meta = typeMeta(t.type);
          return (
            <div key={idx} className="bg-gray-800 border border-gray-700 rounded-lg p-3 space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-xl">{meta.icon}</span>
                <select
                  value={t.type}
                  onChange={(e) => patchTransport(idx, { type: e.target.value })}
                  className="px-2 py-1 rounded bg-gray-700 border border-gray-600 text-white text-xs"
                >
                  {TRANSPORT_TYPES.map(tt => <option key={tt.value} value={tt.value}>{tt.icon} {tt.label}</option>)}
                </select>
                <label className="inline-flex items-center gap-1 text-[11px] text-gray-300">
                  <input type="checkbox" checked={!!t.is_best} onChange={(e) => patchTransport(idx, { is_best: e.target.checked })} />
                  Best
                </label>
                <label className="inline-flex items-center gap-1 text-[11px] text-gray-300">
                  <input type="checkbox" checked={t.direct !== false} onChange={(e) => patchTransport(idx, { direct: e.target.checked })} />
                  Direct
                </label>
                <button
                  onClick={() => removeTransport(idx)}
                  className="ml-auto text-xs text-red-300 hover:text-red-200"
                  title="Remove this mode"
                >
                  🗑️ Remove
                </button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <Field label="Time (minutes)">
                  <NumberInput value={t.time_minutes ?? ''} onChange={(e) => patchTransport(idx, { time_minutes: e.target.value })} min={0} placeholder="e.g. 160" />
                </Field>
                <Field label="Frequency / day">
                  <NumberInput value={t.frequency_per_day ?? ''} onChange={(e) => patchTransport(idx, { frequency_per_day: e.target.value })} min={0} />
                </Field>
                <Field label="Operator">
                  <TextInput value={t.operator || ''} onChange={(e) => patchTransport(idx, { operator: e.target.value })} placeholder="ONCF / CTM / Supratours…" />
                </Field>
                <Field label={t.time_minutes ? `≈ ${_fmtDuration(parseInt(t.time_minutes, 10) || 0)}` : 'Time preview'}>
                  <div className="px-3 py-2 rounded-lg bg-gray-900/50 border border-gray-700 text-gray-400 text-xs">
                    {t.time_minutes ? _fmtDuration(parseInt(t.time_minutes, 10) || 0) : '—'}
                  </div>
                </Field>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <Field label="From station / location">
                  <TextInput value={t.station_from || ''} onChange={(e) => patchTransport(idx, { station_from: e.target.value })} placeholder="Casa Voyageurs" />
                </Field>
                <Field label="To station / location">
                  <TextInput value={t.station_to || ''} onChange={(e) => patchTransport(idx, { station_to: e.target.value })} placeholder="Marrakech" />
                </Field>
              </div>
            </div>
          );
        })}
        <div className="flex gap-2">
          {TRANSPORT_TYPES.map(tt => (
            <button
              key={tt.value}
              onClick={() => addTransport(tt.value)}
              className="px-3 py-1.5 rounded-lg bg-gray-700 hover:bg-gray-600 text-white text-xs font-bold"
            >
              + {tt.icon} {tt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-2 pt-1">
        <button
          onClick={onSave}
          disabled={busy || !route.from_city || !route.to_city || route.from_city === route.to_city}
          className="flex-1 px-4 py-2.5 rounded-lg bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-bold"
        >
          {busy ? 'Saving…' : (route.__new ? '+ Create route' : '💾 Save changes')}
        </button>
        <button
          onClick={onCancel}
          className="px-4 py-2.5 rounded-lg bg-gray-700 hover:bg-gray-600 text-white text-sm font-bold"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default AdminTravelToolsDistance;
